import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ModalRef } from "../../components/Modal";
import { useSearchParams } from "../../hooks";
import { useTranslation } from "react-i18next";
import { APIs } from "../../apis";
import { useApi } from "../../hooks";

export type PaymentSuccessViewModel = {
  modalRef: React.RefObject<ModalRef | null>;
  printTargetRef: React.RefObject<HTMLDivElement | null>;
  code: string;
  checklistKeys: ReadonlyArray<string>;
  t: (key: string, options?: Record<string, unknown>) => string;
  printConfirmation: () => void;
  close: () => void;
};

export default function useViewModel(): PaymentSuccessViewModel {
  const { t } = useTranslation();
  const modalRef = useRef<ModalRef>(null);
  const printTargetRef = useRef<HTMLDivElement>(null);
  const { fetch } = useApi();
  const { searchParams, setSearchParams } = useSearchParams({
    paymentSuccess: false,
    order_id: "",
    code: "",
    telehealth_order: false,
    free: false,
  });

  const close = useCallback(() => {
    setSearchParams({
      paymentSuccess: null,
      order_id: null,
      code: null,
      telehealth_order: null,
      free: null,
    });
    modalRef.current?.close();
  }, [setSearchParams]);

  const getData = useCallback(async () => {
    if (
      !searchParams.paymentSuccess ||
      !searchParams.order_id ||
      !searchParams.code
    ) {
      close();
      return;
    }
    setSearchParams({
      payment_intent: null,
      payment_intent_client_secret: null,
      redirect_status: null,
    });
    modalRef.current?.open();
  }, [
    close,
    setSearchParams,
    searchParams.order_id,
    searchParams.paymentSuccess,
    searchParams.code,
  ]);

  useEffect(() => {
    void getData();
    const modal = modalRef.current;
    return () => {
      modal?.close();
    };
  }, [getData]);

  const checklistKeys = useMemo(
    () =>
      [
        "paymentSuccess.invoiceGenerated",
        "paymentSuccess.clinicNotifiedOfPatientArrival",
        "paymentSuccess.patientNotifiedOfBookingAndLocation",
      ] as const,
    [],
  );

  const printConfirmation = useCallback(async () => {
    try {
      if (!searchParams.order_id) return;
      const res = await fetch(
        () =>
          APIs.order.printSuccessOrderPdf(searchParams.order_id, {
            telehealth_order: !!searchParams.telehealth_order,
            free: !!searchParams.free,
          }),
        {
          successMsg: (r) =>
            t("paymentSuccess.downloadExpires", {
              seconds: r.expires_in_seconds,
            }),
          showErrorAlert: true,
        },
      );

      if (!res?.download_url) return;
      window.open(res.download_url, "_blank", "noopener,noreferrer");
    } catch {}
  }, [
    fetch,
    searchParams.free,
    searchParams.order_id,
    searchParams.telehealth_order,
    t,
  ]);

  return {
    t,
    modalRef,
    printTargetRef,
    code: searchParams.code ?? "-",
    checklistKeys,
    close,
    printConfirmation,
  };
}
